import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, Link } from 'react-router-dom';
import { apps, affiliates as affiliatesApi, referralCodes } from '../api/client';

const CODE_TYPES = ['customer', 'marketing', 'affiliate'];
const REWARD_TYPES = ['percent_off', 'account_credit', 'commission'];

const TYPE_BADGE = {
  customer: 'bg-blue-100 text-blue-800',
  marketing: 'bg-purple-100 text-purple-800',
  affiliate: 'bg-green-100 text-green-800',
};

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function rewardSummary(c) {
  if (c.reward_type === 'commission') {
    const parts = [];
    if (c.commission_percent > 0) parts.push(`${c.commission_percent}%`);
    if (c.commission_cents > 0) parts.push(`${dollars(c.commission_cents)} flat`);
    return `Commission: ${parts.join(' + ') || '—'}`;
  }
  const label = c.reward_type === 'account_credit' ? 'Credit' : '% off';
  const ref = c.referrer_discount_percent > 0 ? `${c.referrer_discount_percent}%` : (c.referrer_discount_cents > 0 ? dollars(c.referrer_discount_cents) : '—');
  const pro = c.prospect_discount_percent > 0 ? `${c.prospect_discount_percent}%` : (c.prospect_discount_cents > 0 ? dollars(c.prospect_discount_cents) : '—');
  return `${label} — referrer ${ref}, prospect ${pro}`;
}

function ownerLabel(c) {
  if (c.code_type === 'affiliate') return c.affiliate_name || `Affiliate #${c.affiliate_id}`;
  if (c.code_type === 'customer') return c.client_account_name || `Account #${c.client_account_id}`;
  return c.campaign || c.marketing_source || '—';
}

export default function ReferralCodesPage() {
  const { appId } = useParams();
  const [app, setApp] = useState(null);
  const [codes, setCodes] = useState([]);
  const [affiliateOptions, setAffiliateOptions] = useState([]);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [codeTypeFilter, setCodeTypeFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [statsCode, setStatsCode] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (codeTypeFilter) filters.code_type = codeTypeFilter;
      if (activeOnly) filters.active_only = true;
      const [appRes, codesRes, affiliatesRes, clientsRes] = await Promise.all([
        apps.get(appId),
        referralCodes.list(appId, filters),
        affiliatesApi.list(true),
        apps.clientAccounts(appId),
      ]);
      setApp(appRes.data.app);
      setCodes(codesRes.data);
      setAffiliateOptions(affiliatesRes.data);
      setClientAccounts(clientsRes.data.client_accounts || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load referral codes');
    } finally {
      setLoading(false);
    }
  }, [appId, codeTypeFilter, activeOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeactivate = async (code) => {
    if (!confirm(`Deactivate code "${code.code}"? It will stop resolving.`)) return;
    try {
      await referralCodes.delete(appId, code.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate code');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div></div>;

  return (
    <div className="p-6">
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">Apps</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">{app?.name}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-600">Referral Codes</span>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Referral Codes</h1>
        <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">+ New Code</button>
      </div>

      <p className="text-gray-600 mb-6">
        Customer refer-a-friend codes, marketing-campaign codes, and affiliate commission codes for this app. Codes are unique per app.
      </p>

      <div className="flex gap-4 items-center mb-4">
        <select value={codeTypeFilter} onChange={(e) => setCodeTypeFilter(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All types</option>
          {CODE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="flex items-center text-sm text-gray-700">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="ml-2">Active only</span>
        </label>
      </div>

      {codes.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No referral codes yet.</p>
          <button onClick={() => setShowCreateModal(true)} className="text-blue-600 hover:text-blue-800">Create a code</button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {codes.map((code) => (
            <div key={code.id} className="p-4 flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{code.code}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${TYPE_BADGE[code.code_type] || 'bg-gray-100 text-gray-700'}`}>{code.code_type}</span>
                  {!code.active && <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Inactive</span>}
                </div>
                <p className="text-sm text-gray-500 mt-1">{ownerLabel(code)} · {rewardSummary(code)}</p>
              </div>
              <div className="flex gap-3 items-center">
                <button onClick={() => setStatsCode(code)} className="text-gray-600 hover:text-gray-800 text-sm">Stats</button>
                <button onClick={() => setEditingCode(code)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                {code.active && <button onClick={() => handleDeactivate(code)} className="text-red-600 hover:text-red-800 text-sm">Deactivate</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreateModal || editingCode) && (
        <ReferralCodeModal
          appId={appId}
          code={editingCode}
          affiliateOptions={affiliateOptions}
          clientAccounts={clientAccounts}
          onClose={() => { setShowCreateModal(false); setEditingCode(null); }}
          onSaved={() => { setShowCreateModal(false); setEditingCode(null); loadData(); }}
        />
      )}

      {statsCode && (
        <StatsModal appId={appId} code={statsCode} onClose={() => setStatsCode(null)} />
      )}
    </div>
  );
}

function ReferralCodeModal({ appId, code, affiliateOptions, clientAccounts, onClose, onSaved }) {
  const isEdit = !!code;
  const [codeStr, setCodeStr] = useState(code?.code || '');
  const [codeType, setCodeType] = useState(code?.code_type || 'marketing');
  const [rewardType, setRewardType] = useState(code?.reward_type || 'percent_off');
  const [clientAccountId, setClientAccountId] = useState(code?.client_account_id || '');
  const [affiliateId, setAffiliateId] = useState(code?.affiliate_id || '');
  const [destination, setDestination] = useState(code?.destination || '');
  const [marketingSource, setMarketingSource] = useState(code?.marketing_source || '');
  const [campaign, setCampaign] = useState(code?.campaign || '');
  const [active, setActive] = useState(code ? code.active : true);
  // Money inputs kept as dollars; converted to cents on submit.
  const [refPct, setRefPct] = useState(code?.referrer_discount_percent || 0);
  const [refDollars, setRefDollars] = useState((code?.referrer_discount_cents || 0) / 100);
  const [refMaxPct, setRefMaxPct] = useState(code?.referrer_max_discount_percent ?? 100);
  const [proPct, setProPct] = useState(code?.prospect_discount_percent || 0);
  const [proDollars, setProDollars] = useState((code?.prospect_discount_cents || 0) / 100);
  const [proMonths, setProMonths] = useState(code?.prospect_discount_months ?? 1);
  const [commPct, setCommPct] = useState(code?.commission_percent || 0);
  const [commDollars, setCommDollars] = useState((code?.commission_cents || 0) / 100);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Affiliate codes are always commission reward.
  const effectiveReward = codeType === 'affiliate' ? 'commission' : rewardType;

  const setType = (t) => {
    setCodeType(t);
    if (t === 'affiliate') setRewardType('commission');
    else if (rewardType === 'commission') setRewardType('percent_off');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload = {
        code: codeStr,
        code_type: codeType,
        reward_type: effectiveReward,
        destination,
        active,
        client_account_id: codeType === 'customer' ? clientAccountId : null,
        affiliate_id: codeType === 'affiliate' ? affiliateId : null,
        marketing_source: codeType === 'marketing' ? marketingSource : '',
        campaign: codeType === 'marketing' ? campaign : '',
      };
      if (effectiveReward === 'commission') {
        payload.commission_percent = Number(commPct) || 0;
        payload.commission_cents = Math.round((Number(commDollars) || 0) * 100);
      } else {
        payload.referrer_discount_percent = Number(refPct) || 0;
        payload.referrer_discount_cents = Math.round((Number(refDollars) || 0) * 100);
        payload.referrer_max_discount_percent = Number(refMaxPct) || 0;
        payload.prospect_discount_percent = Number(proPct) || 0;
        payload.prospect_discount_cents = Math.round((Number(proDollars) || 0) * 100);
        payload.prospect_discount_months = Number(proMonths) || 1;
      }
      if (isEdit) {
        await referralCodes.update(appId, code.id, payload);
      } else {
        await referralCodes.create(appId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || err.response?.data?.error || 'Failed to save code');
    } finally {
      setSaving(false);
    }
  };

  const missingOwner = (codeType === 'customer' && !clientAccountId) || (codeType === 'affiliate' && !affiliateId);

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Referral Code' : 'New Referral Code'}
      size="lg"
    >
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
              <input type="text" value={codeStr} onChange={(e) => setCodeStr(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., summer-promo" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={codeType} onChange={(e) => setType(e.target.value)} disabled={isEdit} className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100">
                {CODE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {codeType === 'customer' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Client account (referrer)</label>
              <select value={clientAccountId} onChange={(e) => setClientAccountId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" required>
                <option value="">Select an account…</option>
                {clientAccounts.map((ca) => <option key={ca.id} value={ca.id}>{ca.name || ca.external_id}</option>)}
              </select>
            </div>
          )}

          {codeType === 'affiliate' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate</label>
              <select value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" required>
                <option value="">Select an affiliate…</option>
                {affiliateOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          {codeType === 'marketing' && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <input type="text" value={marketingSource} onChange={(e) => setMarketingSource(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="facebook, radio…" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                <input type="text" value={campaign} onChange={(e) => setCampaign(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="jan-launch" />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Reward type</label>
            <select value={effectiveReward} onChange={(e) => setRewardType(e.target.value)} disabled={codeType === 'affiliate'} className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100">
              {REWARD_TYPES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
            {codeType === 'affiliate' && <p className="mt-1 text-xs text-gray-500">Affiliate codes always pay commission.</p>}
          </div>

          {effectiveReward === 'commission' ? (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission %</label>
                <input type="number" min="0" max="100" value={commPct} onChange={(e) => setCommPct(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flat commission ($)</label>
                <input type="number" min="0" step="0.01" value={commDollars} onChange={(e) => setCommDollars(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
          ) : (
            <div className="mb-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Referrer reward</p>
                <div className="grid grid-cols-3 gap-3">
                  <input type="number" min="0" max="100" value={refPct} onChange={(e) => setRefPct(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="%" title="Discount %" />
                  <input type="number" min="0" step="0.01" value={refDollars} onChange={(e) => setRefDollars(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="$" title="Flat $" />
                  <input type="number" min="0" max="100" value={refMaxPct} onChange={(e) => setRefMaxPct(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="max %" title="Max discount %" />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Prospect reward</p>
                <div className="grid grid-cols-3 gap-3">
                  <input type="number" min="0" max="100" value={proPct} onChange={(e) => setProPct(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="%" title="Discount %" />
                  <input type="number" min="0" step="0.01" value={proDollars} onChange={(e) => setProDollars(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="$" title="Flat $" />
                  <input type="number" min="1" value={proMonths} onChange={(e) => setProMonths(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="months" title="Duration (months)" />
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination (optional)</label>
            <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="/pricing or https://…" />
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving || !codeStr || missingOwner} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Save' : 'Create')}
            </button>
          </div>
      </form>
    </Modal>
  );
}

function StatsModal({ appId, code, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    referralCodes.get(appId, code.id)
      .then((res) => { if (live) setStats(res.data); })
      .catch((err) => { if (live) setError(err.response?.data?.error || 'Failed to load stats'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [appId, code.id]);

  return (
    <Modal
      open
      onClose={onClose}
      title={<>Stats — <span className="font-mono">{code.code}</span></>}
      size="lg"
      footer={<><button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Close</button></>}
    >
        {loading && <div className="text-gray-500 py-4">Loading…</div>}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>}
        {stats && (
          <>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.total_referrals}</p>
                <p className="text-sm text-gray-500">Total referrals</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.active_referrals}</p>
                <p className="text-sm text-gray-500">Active subscriptions</p>
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Recent referrals</h3>
            {(!stats.recent_referrals || stats.recent_referrals.length === 0) ? (
              <p className="text-sm text-gray-500">No referrals yet.</p>
            ) : (
              <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {stats.recent_referrals.map((r) => (
                  <div key={r.id} className="py-2 flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium">{r.name || r.external_id}</span>
                      {r.email && <span className="text-gray-500"> · {r.email}</span>}
                    </div>
                    <span className={r.has_active_subscription ? 'text-green-600' : 'text-gray-400'}>
                      {r.has_active_subscription ? 'active' : 'inactive'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
    </Modal>
  );
}
